import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { parseDirectoryViewMode } from '../../shared/session/directoryViewMode.js';
import {
  inferPaymentResumeReturnSource,
  isMapResumeReturnPath,
  isPaymentResumeReturnPath,
  parkMapResumeReturnTarget,
  parkPaymentResumeReturnTarget,
  savePlatformReturnTarget,
  saveReturnTarget,
  type ReturnTargetSource,
} from '../../shared/session/returnTarget.js';
import { resolveCustomerSignInPath } from '../auth/customerAuthPaths.js';
import { useAccountMe } from '../account/accountApi.js';
import {
  resolveHeaderAccountBadgeText,
  resolveHeaderAccountIdentity,
} from './headerAccountBadge.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import { isGuestAuthPath } from '../auth/routing/isGuestAuthPath.js';
import {
  CUSTOMER_TOP_BAR_AUTH_AVATAR_CLASS,
  CUSTOMER_TOP_BAR_AUTH_LINK_CLASS,
} from '../../app/customerTopBarStyles.js';

/** Non-payment parks only — payment sources come from {@link inferPaymentResumeReturnSource}. */
function inferNonPaymentReturnTargetSource(pathname: string): ReturnTargetSource {
  if (pathname.includes('/account')) {
    return 'account';
  }
  return 'shop';
}

const headerAuthLinkClass = CUSTOMER_TOP_BAR_AUTH_LINK_CLASS;

const headerAccountAvatarClass = CUSTOMER_TOP_BAR_AUTH_AVATAR_CLASS;

/** Wave D — platform account chrome (no `/{jwt}/account`). Sign-in uses path SSOT. */
const PLATFORM_ACCOUNT_HREF = '/account';

interface JourneyHeaderAuthLinkProps {
  readonly context?: 'POST_KIOSK_PRODUCT' | 'POST_KIOSK_DONATION' | 'PHONE_FIRST_PRODUCT' | 'PHONE_FIRST_DONATION';
  readonly forceShopTestId?: boolean;
}

export function JourneyHeaderAuthLink({
  context,
  forceShopTestId = false,
}: JourneyHeaderAuthLinkProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  const tenantCode = useTenantCode();
  const location = useLocation();
  const { session } = useCustomerSession();
  // Spec A: session after `/me` is chrome authority. Keep signed-in chrome while a
  // live session exists even if SoftRace briefly flips `sessionHydrated` false
  // (parity with CustomerProfileButton / BottomNav). Pre-hydrate with session == null
  // stays guest — `isGloballySignedInChrome` is false until `/me` settles.
  const signedIn = isGloballySignedInChrome({ session });
  const meQuery = useAccountMe(signedIn);
  const signInHref = resolveCustomerSignInPath(tenantCode);

  const onShopRoute =
    forceShopTestId ||
    (location.pathname.includes('/shop') && !location.pathname.includes('/shop/scanner'));
  const headerAccountTestId = onShopRoute ? 'customer-shop-header-account' : undefined;

  // Guest-auth routes: no header auth chrome (avatar / Sign in) above the form —
  // covers hydrate wait and RequireGuestAuth redirect residual.
  if (isGuestAuthPath(location.pathname)) {
    return null;
  }

  if (signedIn) {
    const identity = resolveHeaderAccountIdentity({
      username: meQuery.data?.username,
      email: meQuery.data?.email,
    });
    const badgeText = resolveHeaderAccountBadgeText(identity);
    const accountName = identity.length > 0 ? identity : t('journey.myAccount');

    return (
      <Link
        to={PLATFORM_ACCOUNT_HREF}
        className={headerAccountAvatarClass}
        data-context={context}
        data-testid={headerAccountTestId}
        aria-label={t('journey.myAccountAria', { name: accountName })}
        title={accountName}
      >
        <span aria-hidden="true">{badgeText}</span>
      </Link>
    );
  }

  return (
    <Link
      to={signInHref}
      className={headerAuthLinkClass}
      data-context={context}
      data-testid={headerAccountTestId}
      onClick={(e) => {
        const path = `${location.pathname}${location.search}`;
        // G1 — shared predicate: journey /checkout|/donate elevate; account hubs
        // (e.g. /acme/account/donations) must not.
        const resumeSource = inferPaymentResumeReturnSource(path);
        if (resumeSource !== null && isPaymentResumeReturnPath(path)) {
          if (!isPlatformChromeTenant(tenantCode)) {
            const parked = parkPaymentResumeReturnTarget(path, {
              tenantCode,
              source: resumeSource,
            });
            if (parked === null) {
              e.preventDefault();
            }
          }
          return;
        }
        // G5 — map directory chrome: same elevate as Views CTA; bare list does not.
        if (
          isMapResumeReturnPath(path) &&
          parseDirectoryViewMode(location.search) === 'map'
        ) {
          const parked = parkMapResumeReturnTarget(path, {
            tenantCode,
            source: 'shop',
          });
          if (parked === null) {
            e.preventDefault();
          }
          return;
        }
        if (savePlatformReturnTarget(path) !== null) {
          return;
        }
        if (!isPlatformChromeTenant(tenantCode)) {
          saveReturnTarget(path, {
            tenantCode,
            source: inferNonPaymentReturnTargetSource(location.pathname),
          });
        }
      }}
    >
      {t('journey.signIn')}
    </Link>
  );
}
